import { ref, onMounted, onUnmounted } from 'vue'
import { fromNow } from '@/utils/formatter'
import EventBus from '@/libs/AppEventBus'
import { useAuthStore } from '@/modules/auth/authStore'
import ability from '@/libs/acl/ability'
import { service } from './service'

const EXPORT_TYPE_LABELS = {
    PACKAGE_SALE: 'Public Package Sale',
    BOOKING: 'MCU Booking',
    RETAIL_SALE: 'Retail Sale',
    INBOUND_REFERRAL: 'Inbound Referral',
    COUPON_REDEMPTION: 'Coupon Redemption',
    EARN_TRANSACTION: 'Earn Transaction',
    LOYALTY_ACCOUNT: 'Loyalty Account'
}

const STATUS_LABELS = {
    PENDING: 'Pending',
    IN_PROGRESS: 'In Progress',
    COMPLETE: 'Complete',
    FAILED: 'Failed'
}

const STATUS_SEVERITY = {
    PENDING: 'info',
    IN_PROGRESS: 'warning',
    COMPLETE: 'success',
    FAILED: 'danger'
}

export default function useExportHistory() {
    const authStore = useAuthStore()
    const user = authStore.getUserData
    const panel = ref()
    const exportHistories = ref([])
    const page = ref(0)
    const perPage = ref(5)
    const lastPage = ref(false)
    const isLoading = ref(false)
    const downloadingIds = ref([])
    const hasNewExport = ref(false)

    const markNewExport = () => {
        hasNewExport.value = true
    }

    const clearNewExport = () => {
        hasNewExport.value = false
    }

    const onExportQueued = async () => {
        markNewExport()
        page.value = 0
        lastPage.value = false

        if (exportHistories.value.length === 0) {
            return
        }

        exportHistories.value = []
        await fetchExportHistories()
    }

    const togglePanel = async (event) => {
        clearNewExport()
        panel.value.toggle(event)
        if (page.value === 0 && !isLoading.value && !lastPage.value) {
            await fetchExportHistories()
        }
    }

    const handleScroll = () => {
        if (panel.value) {
            panel.value.hide()
        }
    }

    const upsertExportHistoryItem = (payload) => {
        const exportHistoryId = String(payload?.export_history_id || payload?.id || '')
        if (!exportHistoryId) {
            return
        }

        const item = normalizeExportHistory({
            id: exportHistoryId,
            title: payload.title,
            message: payload.message,
            export_type: payload.export_type,
            status: payload.status || (payload.download_url ? 'COMPLETE' : 'FAILED'),
            created_at: payload.created_at || new Date().toISOString(),
            download_url: payload.download_url || null
        })

        const index = exportHistories.value.findIndex((val) => String(val.id) === exportHistoryId)
        if (index >= 0) {
            exportHistories.value[index] = {
                ...exportHistories.value[index],
                ...item
            }
            markNewExport()
            return
        }

        const newArr = exportHistories.value.slice()
        newArr.unshift(item)
        exportHistories.value = newArr
        markNewExport()
    }

    const appendOrUpdateExportHistory = (data) => {
        const notification = data.notification
        let notiData = {}

        try {
            notiData = JSON.parse(notification.data)
        } catch {
            return
        }

        if (notiData.type !== 'export' || String(notiData.requested_by) !== String(user.id)) {
            return
        }

        upsertExportHistoryItem({
            ...notiData,
            created_at: notification.created_at
        })
    }

    const handleExportStatusUpdated = (payload) => {
        if (String(payload?.user_id) !== String(user.id)) {
            return
        }

        upsertExportHistoryItem(payload)

        const isFailed = payload.status === 'FAILED'
        EventBus.emit('show-toast', {
            severity: isFailed ? 'error' : 'info',
            summary: payload.title || (isFailed ? 'Export Failed' : 'Export Ready'),
            detail: payload.message || (isFailed ? 'Export failed. Please try again.' : 'Your export is ready for download.')
        })
    }

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
        EventBus.on('export-history-queued', onExportQueued)

        if (!window.Echo || !user?.id) {
            return
        }

        window.Echo.private('notification.' + user.id).listen('.export.status.updated', handleExportStatusUpdated)

        if (user.role == 'doctor') {
            window.Echo.private('notification.' + user.id).listen('.new.notification', onDoctorExportNotification)
        } else {
            window.Echo.private('notification.portal').listen('.new.notification', onPortalExportNotification)
        }
    })

    const onDoctorExportNotification = (e) => {
        if (user.id == e.notification.notifiable && e.notification.type == 'export') {
            appendOrUpdateExportHistory(e)
        }
    }

    const onPortalExportNotification = (e) => {
        if (user.corporate_ids.length == 0 && e.notification.notifiable == 'portal') {
            const action = e.notification.permission.substring(0, e.notification.permission.indexOf('-'))
            if (ability.can(action, 'notifications') && e.notification.type == 'export') {
                appendOrUpdateExportHistory(e)
            }
        }
    }

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
        EventBus.off('export-history-queued', onExportQueued)
        if (window.Echo && user?.id) {
            window.Echo.private('notification.' + user.id).stopListening('.export.status.updated')
            if (user.role == 'doctor') {
                window.Echo.private('notification.' + user.id).stopListening('.new.notification', onDoctorExportNotification)
            } else {
                window.Echo.private('notification.portal').stopListening('.new.notification', onPortalExportNotification)
            }
        }
    })

    const statusLabel = (status) => STATUS_LABELS[status] || status
    const statusSeverity = (status) => STATUS_SEVERITY[status] || 'secondary'
    const exportTypeLabel = (exportType) => EXPORT_TYPE_LABELS[exportType] || exportType
    const displayTitle = (item) => item?.title || exportTypeLabel(item?.export_type) || 'Export'
    const displayMessage = (item) => item?.message || ''
    const actionLabel = (item) => {
        if (item?.status === 'COMPLETE') {
            const fileName = item?.file_name || ''
            const extension = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : ''
            return extension ? `Download (${extension})` : 'Download'
        }

        return 'Processing'
    }

    const normalizeExportHistory = (item) => {
        const exportType = item?.export_type || item?.exportType || item?.type || ''

        return {
            ...item,
            title: item?.title || exportTypeLabel(exportType) || 'Export',
            message: item?.message || '',
            export_type: exportType,
            status: item?.status || 'PENDING',
            created_at: item?.created_at || item?.createdAt,
            file_name: item?.file_name || item?.fileName,
            download_url: item?.download_url || item?.downloadUrl || null
        }
    }

    const resolveListPayload = (response) => {
        const candidates = [response, response?.data, response?.data?.data, response?.data?.exportHistories, response?.exportHistories, response?.export_histories]

        for (const candidate of candidates) {
            if (Array.isArray(candidate?.data)) {
                return candidate
            }

            if (Array.isArray(candidate)) {
                return {
                    data: candidate,
                    total: candidate.length
                }
            }
        }

        return {
            data: [],
            total: 0
        }
    }

    const fetchExportHistories = async () => {
        if (isLoading.value || lastPage.value) {
            return
        }

        isLoading.value = true
        page.value++

        const response = await service.getAll({
            page: page.value,
            per_page: perPage.value
        })

        const payload = resolveListPayload(response)
        const list = Array.isArray(payload?.data) ? payload.data.map(normalizeExportHistory) : []
        const total = Number(payload?.total ?? payload?.meta?.total ?? payload?.pagination?.total ?? page.value * perPage.value)
        const existingIds = new Set(exportHistories.value.map((item) => String(item.id)))
        const newItems = list.filter((item) => !existingIds.has(String(item.id)))

        exportHistories.value.push(...newItems)
        lastPage.value = page.value * perPage.value >= total || list.length < perPage.value
        isLoading.value = false
    }

    const canDownload = (item) => item.status === 'COMPLETE'
    const triggerBrowserDownload = (url, fileName) => {
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    const isDownloading = (id) => downloadingIds.value.includes(String(id))

    const setDownloading = (id, value) => {
        const normalizedId = String(id)

        if (value) {
            if (!downloadingIds.value.includes(normalizedId)) {
                downloadingIds.value.push(normalizedId)
            }
            return
        }

        downloadingIds.value = downloadingIds.value.filter((itemId) => itemId !== normalizedId)
    }

    const downloadExport = async (item, event) => {
        event?.stopPropagation()

        if (!canDownload(item) || isDownloading(item.id)) {
            return
        }

        setDownloading(item.id, true)

        try {
            const response = await service.download(item.id)
            const payload = response?.data || response
            const downloadUrl = payload?.download_url
            const fileName = payload?.file_name || item.file_name || 'export.xlsx'

            if (!downloadUrl) {
                return
            }

            triggerBrowserDownload(downloadUrl, fileName)
        } finally {
            setDownloading(item.id, false)
        }
    }

    return {
        panel,
        exportHistories,
        hasNewExport,
        isLoading,
        lastPage,
        togglePanel,
        fetchExportHistories,
        fromNow,
        statusLabel,
        statusSeverity,
        exportTypeLabel,
        displayTitle,
        displayMessage,
        actionLabel,
        canDownload,
        isDownloading,
        downloadExport
    }
}
